import { FC, useEffect, useRef, useState, useLayoutEffect } from "react";
import {
  Button,
  Col,
  Container,
  InputGroup,
  Row,
  Stack,
} from "react-bootstrap";
import Logo from "../../components/Logo";
import { useNavigate, useLocation } from "react-router-dom";
import { eyeOff, eyeOn, submitIcon } from "../../utils/Icons";
import CustomError from "../../utils/CustomError";
import { loginDataUpdate, verifyPassword } from "../../api/request";
import { authURL } from "../../api/baseUrl";

const Login: FC = () => {
  const [showCode, setShowCode] = useState(false);
  const [accessCode, setAccessCode] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);
  const [isCallback, setIsCallback] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  // If already authenticated, don't show login at all
  useLayoutEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/chat-mode", { replace: true });
    }
  }, [navigate]);

  // Detect Google callback BEFORE first paint to avoid flash
  useLayoutEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("access_token")) {
      setIsCallback(true); // hide the form instantly
      const params = new URLSearchParams(hash.replace("#", "?"));
      const googleAccessToken = params.get("access_token");
      const googleError = params.get("error");

      if (googleError) {
        setIsCallback(false);
        setError("Google sign-in was cancelled or failed.");
        window.location.hash = "";
        return;
      }
      if (googleAccessToken) {
        // kickoff finalize without showing the form
        finalizeAfterGoogle(googleAccessToken);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run only once, before paint

  // Auto-hide error after 3s
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(""), 3000);
    return () => clearTimeout(t);
  }, [error]);

  // Focus the field (but not during callback mode)
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!isCallback) inputRef.current?.focus();
  }, [isCallback]);

  // Finalize login after we return from Google
  const finalizeAfterGoogle = async (googleAccessToken: string) => {
    try {
      setBusy(true);

      // 1) Get Google profile
      const profileResp = await fetch(
        "https://www.googleapis.com/oauth2/v3/userinfo",
        { headers: { Authorization: `Bearer ${googleAccessToken}` } }
      );
      if (!profileResp.ok) throw new Error("Failed to fetch Google profile.");

      const profile = await profileResp.json();
      if (!profile?.email) throw new Error("Google profile missing email.");

      // 2) Pull universityId we saved earlier
      const universityId = localStorage.getItem("universityId") || "";
      const uniStorageContainer =
        localStorage.getItem("uniStorageContainer") || "";

      // 3) Create/Update user on your backend + mint app token
      const up = await loginDataUpdate({
        user_id: profile.email,
        name: profile.name || profile.email,
        googleId: profile.sub,
        googleEmail: profile.email,
        university_id: universityId || undefined,
        university_storage_container: uniStorageContainer || undefined,
      });

      if (!up?.flag)
        throw new Error(up?.message || "Unable to finalize login.");
      if (!up?.result?.token)
        throw new Error("No token returned by loginDataUpdate.");

      // 4) Store app token & basics
      localStorage.setItem("token", up.result.token);
      localStorage.setItem("email", profile.email);
      localStorage.setItem("name", profile.name || profile.email);
      localStorage.setItem("picture", profile.picture || "");
      localStorage.setItem("normalLogin", "true");
      if (up?.result?.userId)
        localStorage.setItem("userId", String(up.result.userId));

      // 5) Clean URL fragment
      window.location.hash = "";
      // Optionally clear the uniId if you don’t need it anymore:
      // localStorage.removeItem("universityId");

      // 6) Go to the app
      navigate("/chat-mode", { replace: true });
    } catch (e: any) {
      console.error(e);
      setError(e?.message || "Something went wrong during sign-in.");
      setIsCallback(false); // allow form to reappear on failure
    } finally {
      setBusy(false);
    }
  };

  // Step 1: verify access code, then start Google OAuth
  const handleLogin = async () => {
    if (!accessCode?.trim()) {
      setError("Please enter your access code.");
      return;
    }
    try {
      setBusy(true);

      const resp = await verifyPassword({ code: accessCode.trim() });
      const uniName = localStorage.getItem("uniName");
      console.log(resp?.message);
      if (resp.status == 400)
        throw new Error("You have entered the wrong access code.");
      if (resp.status == 403)
        throw new Error(
          "This university is currently inactive. Please contact your admin."
        );
      if (!resp?.flag) throw new Error(resp?.message || "Invalid access code.");

      const uni = resp?.result || resp;
      const universityId =
        uni?.universityId ?? uni?.result?.universityId ?? null;
      if (!universityId)
        throw new Error("Verification succeeded but no universityId returned.");

      localStorage.setItem("universityId", String(universityId));
      localStorage.setItem(
        "uniStorageContainer",
        String(uni.storage_container)
      );

      // Start Google OAuth (implicit flow)
      window.location.href = authURL;
    } catch (e: any) {
      // Prefer backend message if available
      const serverMsg =
        e?.response?.data?.message || // comes from HelperUtils.errorObj
        e?.response?.data?.error ||
        e?.message ||
        "Access code verification failed.";

      console.error(serverMsg);
      setError(serverMsg);
    } finally {
      setBusy(false);
    }
  };

  // --------- RENDER ----------
  // If we're handling the Google callback or busy finalizing, show loader only
  if (isCallback || busy) {
    return (
      <section className="login-page h-100">
        <Container className="h-100">
          <Row className="h-100 align-items-center justify-content-center">
            <Col xxl={4} lg={5} md={8} className="text-center">
              <Logo />
              <div className="mt-5 text-muted">Signing you in…</div>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  return (
    <section className="login-page h-100">
      <Container className="h-100">
        <Row className="h-100 align-items-center justify-content-center">
          <Col xxl={4} lg={5} md={8} className="text-center">
            <Logo />
            <Stack className="mt-5">
              <InputGroup className="mb-3">
                <input
                  className="form-control"
                  autoComplete="one-time-code"
                  type={showCode ? "text" : "password"}
                  placeholder="Access code"
                  value={accessCode}
                  disabled={busy}
                  onChange={(e: any) => setAccessCode(e.target.value)}
                  onKeyDown={(key: any) => {
                    if (key.key === "Enter" && !busy) handleLogin();
                  }}
                  ref={inputRef}
                />
                <Button
                  onClick={() => setShowCode((prev) => !prev)}
                  type="button"
                  variant="eye-icon"
                  disabled={busy}
                >
                  {showCode ? eyeOn() : eyeOff()}
                </Button>
                <Button
                  type="button"
                  variant=""
                  className="submit-btn"
                  disabled={busy}
                  onClick={handleLogin}
                >
                  {submitIcon()}
                </Button>
              </InputGroup>
              {busy && <div className="text-muted small">Processing…</div>}
            </Stack>
            <CustomError
              class={`mt-3 ${error ? "show-error" : ""}`}
              message={error || ""}
            />
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Login;
